import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import {
  LoaderPinwheel,
  User,
  HeartPulse,
  Camera,
  PenLine,
  Save,
  Trash2,
  CheckCircle2,
} from "lucide-react";

const inputClass =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:outline-none transition-colors bg-white";

const labelClass = "block text-xs font-medium text-gray-500 mb-1";

const SectionHeader = ({ icon: Icon, title, colorClass = "bg-blue-50 text-blue-600" }) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
    <div className={`p-1.5 rounded-lg ${colorClass.split(" ")[0]}`}>
      <Icon className={`h-4 w-4 ${colorClass.split(" ")[1]}`} />
    </div>
    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
  </div>
);

const PerfilUsuarioPage = () => {
  const [usuario, setUsuario] = useState({
    cedula: "",
    name: "",
    direccion: "",
    telefono: "",
    email: "",
    grupo_sanguineo: "",
    fecha_nac: "",
    cargo: "",
    contacto_emerg: "",
    telefono_emerg: "",
    firma: "",
    foto: "",
  });

  const sigCanvas = useRef(null);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const API_URL = process.env.REACT_APP_API_URL + "/users";

  const gruposSanguineos = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const cargos = [
    "GERENCIAL",
    "DIRECTIVO",
    "ADMINISTRATIVO",
    "COORDINADOR",
    "OPERATIVO",
    "ASISTENCIAL",
    "AUXILIAR",
    "SERVICIOS GENERALES",
    "PASANTE",
    "APRENDIZ",
    "ASPIRANTE",
    "ASESOR",
    "COMERCIAL",
  ];

  const fetchUsuario = async () => {
    try {
      const res = await axios.get(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = res.data || {};
      setUsuario({
        cedula: userData.cedula || "",
        name: userData.name || "",
        direccion: userData.direccion || "",
        telefono: userData.telefono || "",
        email: userData.email || "",
        grupo_sanguineo: userData.grupo_sanguineo || "",
        fecha_nac: userData.fecha_nac ? userData.fecha_nac.slice(0, 10) : "",
        cargo: userData.cargo || "",
        contacto_emerg: userData.contacto_emerg || "",
        telefono_emerg: userData.telefono_emerg || "",
        firma: userData.firma || "",
        foto: userData.foto || "",
      });
    } catch (err) {
      toast.error("No se pudo cargar la información del usuario");
    }
  };

  useEffect(() => {
    fetchUsuario();
    // eslint-disable-next-line
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        ...usuario,
        role: usuario.role?.name ?? undefined,
      };
      const { data } = await axios.put(`${API_URL}/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Perfil actualizado correctamente");
      const userActualizado = { ...user, ...data };
      localStorage.setItem("user", JSON.stringify(userActualizado));
      setUsuario(userActualizado);
    } catch (err) {
      toast.error("No se pudo actualizar el perfil");
    } finally {
      setLoading(false);
    }
  };

  if (!usuario) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-600">
        <LoaderPinwheel className="w-10 h-10 mb-3 animate-spin text-blue-600" />
        <p>Cargando perfil...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Avatar + resumen */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
        <div>
          {usuario.foto ? (
            <img
              src={usuario.foto}
              alt="Foto de perfil"
              className="w-20 h-20 rounded-full object-cover border-2 border-blue-200 shadow-sm"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-200">
              <User className="h-9 w-9 text-blue-400" />
            </div>
          )}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {usuario.name || "Sin nombre"}
          </h2>
          <p className="text-sm text-gray-500">{usuario.email}</p>
          {usuario.cargo && (
            <span className="inline-block mt-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full font-medium">
              {usuario.cargo}
            </span>
          )}
        </div>
      </div>

      {/* Información personal */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={User} title="Información Personal" colorClass="bg-blue-50 text-blue-600" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Cédula</label>
            <input
              type="number"
              className={inputClass}
              value={usuario.cedula}
              onChange={(e) => setUsuario({ ...usuario, cedula: e.target.value })}
              placeholder="Número de documento"
            />
          </div>

          <div>
            <label className={labelClass}>Nombre completo</label>
            <input
              type="text"
              className={inputClass}
              value={usuario.name}
              onChange={(e) => setUsuario({ ...usuario, name: e.target.value })}
              placeholder="Nombre y apellidos"
            />
          </div>

          <div>
            <label className={labelClass}>Correo electrónico</label>
            <input
              type="email"
              className={`${inputClass} bg-gray-50 text-gray-400 cursor-not-allowed`}
              value={usuario.email}
              readOnly
            />
          </div>

          <div>
            <label className={labelClass}>Fecha de nacimiento</label>
            <input
              type="date"
              className={inputClass}
              value={usuario.fecha_nac}
              onChange={(e) =>
                setUsuario({ ...usuario, fecha_nac: e.target.value })
              }
            />
          </div>

          <div>
            <label className={labelClass}>Dirección</label>
            <input
              type="text"
              className={inputClass}
              value={usuario.direccion}
              onChange={(e) =>
                setUsuario({ ...usuario, direccion: e.target.value })
              }
              placeholder="Dirección de residencia"
            />
          </div>

          <div>
            <label className={labelClass}>Teléfono</label>
            <input
              type="text"
              className={inputClass}
              value={usuario.telefono}
              onChange={(e) =>
                setUsuario({ ...usuario, telefono: e.target.value })
              }
              placeholder="Número de teléfono"
            />
          </div>

          <div>
            <label className={labelClass}>Grupo Sanguíneo</label>
            <select
              className={inputClass}
              value={usuario.grupo_sanguineo}
              onChange={(e) =>
                setUsuario({ ...usuario, grupo_sanguineo: e.target.value })
              }
            >
              <option value="">Seleccione grupo sanguíneo</option>
              {gruposSanguineos.map((grupo) => (
                <option key={grupo} value={grupo}>
                  {grupo}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Cargo</label>
            <select
              className={inputClass}
              value={usuario.cargo}
              onChange={(e) =>
                setUsuario({ ...usuario, cargo: e.target.value })
              }
            >
              <option value="">Seleccione un cargo</option>
              {cargos.map((cargo) => (
                <option key={cargo} value={cargo}>
                  {cargo}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Contacto de emergencia */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={HeartPulse} title="Contacto de Emergencia" colorClass="bg-red-50 text-red-500" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Nombre del contacto</label>
            <input
              type="text"
              className={inputClass}
              value={usuario.contacto_emerg}
              onChange={(e) =>
                setUsuario({ ...usuario, contacto_emerg: e.target.value })
              }
              placeholder="Nombre completo"
            />
          </div>

          <div>
            <label className={labelClass}>Teléfono de emergencia</label>
            <input
              type="text"
              className={inputClass}
              value={usuario.telefono_emerg}
              onChange={(e) =>
                setUsuario({ ...usuario, telefono_emerg: e.target.value })
              }
              placeholder="Número de contacto"
            />
          </div>
        </div>
      </div>

      {/* Foto de perfil */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={Camera} title="Foto de Perfil" colorClass="bg-purple-50 text-purple-600" />
        <div className="flex flex-col sm:flex-row items-start gap-5">
          {usuario.foto && (
            <div className="flex-shrink-0">
              <img
                src={usuario.foto}
                alt="Foto de perfil"
                className="w-24 h-24 rounded-xl object-cover border border-gray-200 shadow-sm"
              />
              <p className="text-xs text-gray-400 mt-1.5 text-center">Actual</p>
            </div>
          )}
          <div className="flex-1">
            <label className={labelClass}>Subir nueva foto</label>
            <input
              type="file"
              accept="image/*"
              className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-200 rounded-lg cursor-pointer transition-colors"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onloadend = () => {
                  setUsuario({ ...usuario, foto: reader.result });
                };
                reader.readAsDataURL(file);
              }}
            />
            <p className="text-xs text-gray-400 mt-1.5">
              Formatos aceptados: JPG, PNG, WEBP
            </p>
          </div>
        </div>
      </div>

      {/* Firma digital */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={PenLine} title="Firma Digital" colorClass="bg-green-50 text-green-600" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Firma guardada */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">
              Firma guardada
            </p>
            {usuario.firma ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <img
                  src={usuario.firma}
                  alt="Firma del usuario"
                  className="w-full h-32 object-contain p-2"
                />
              </div>
            ) : (
              <div className="border border-dashed border-gray-200 rounded-xl h-32 flex items-center justify-center bg-white">
                <p className="text-sm text-gray-400">Sin firma guardada</p>
              </div>
            )}
          </div>

          {/* Canvas para nueva firma */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">
              Trazar nueva firma
            </p>
            <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm h-32">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  className: "w-full h-full touch-none",
                }}
              />
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => sigCanvas.current.clear()}
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Limpiar
              </button>
              <button
                onClick={() => {
                  if (!sigCanvas.current.isEmpty()) {
                    const dataURL = sigCanvas.current.toDataURL("image/png");
                    setUsuario({ ...usuario, firma: dataURL });
                    toast.success("Firma capturada");
                  } else {
                    toast.error("La firma está vacía");
                  }
                }}
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <CheckCircle2 className="h-3 w-3" />
                Capturar firma
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Botón guardar */}
      <div className="flex justify-end pt-1">
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
        >
          {loading ? (
            <LoaderPinwheel className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {loading ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
};

export default PerfilUsuarioPage;
